import { useState, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";
import { invalidateLists, invalidateListsSavedByTurn, listTouchedByTool } from "@/lib/chat-saved-lists";
import type { ConversationMessage, ConversationDetail, ToolCallInfo } from "./use-conversations";
import type { ChatTurnMeta } from "@shared/chat-turn-meta";
import { AUTO_MODEL } from "@shared/model-config";
import { d1Date } from "@/lib/d1-timestamp";

// Re-export types that DataAnalyst.tsx uses
export type { ToolCallInfo, ChatTurnMeta };

/** #2090: what the chip shows for a catalogued (large) spreadsheet. */
export interface AttachmentCatalogueSummary {
  sheets: Array<{ name: string; row_count: number }>;
}

export interface Attachment {
  type: "image" | "document" | "text";
  mediaType: string;  // e.g. "image/jpeg", "application/pdf", "text/csv"
  data: string;       // base64 for images/PDFs, raw text for CSV/TXT
  filename: string;
  sizeBytes: number;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  attachments?: Array<{
    type: string; mediaType: string; filename: string; sizeBytes: number;
    catalogue?: AttachmentCatalogueSummary; catalogueError?: string;
    /** #2555: stamped by the server's durable-copy step (live event, or the stored row on reload). */
    conversion_status?: string; conversion_reason?: string; bytes?: number;
  }>;
  tool_calls?: ToolCallInfo[];
  charts?: Array<{ spec: unknown; sqlQuery?: string }>;
  model?: string;
  duration_ms?: number;
  timestamp: number;
  conversationId?: string;
  follow_up_suggestions?: string[];
  /** #1996: turn-end metadata (5-minute cap hit, artifacts saved) — banner under the body. */
  meta?: ChatTurnMeta;
  /**
   * Flagged true when the SSE stream ended without a "done" event but content
   * had already been streamed (Akira 2026-05-26 bug). UI surfaces a small
   * "Response cut short" banner above the message. The partial content is
   * preserved so the user doesn't lose work.
   */
  _partial?: boolean;
  /**
   * Human-readable reason describing what was happening when the stream
   * cut. Derived from the streaming-state at close time — useful so users
   * see "the AI was running a tool when it stopped" instead of just
   * "Response was cut short" (#199 task c).
   */
  _partial_reason?: string;
}

export interface StreamingState {
  content: string;
  liveTools: Array<{
    tool: string;
    input_summary: string;
    status: "running" | "done";
    row_count?: number;
    execution_ms?: number;
  }>;
  liveCharts: Array<{ spec: unknown; sqlQuery?: string }>;
}

/**
 * #2292 finding 7: tags a send failure caused by the attachment count/size
 * cap, distinct from a generic transport or model error, so the caller can
 * tell sendMessage() to restore the files it just cleared rather than
 * forcing a re-pick.
 */
class AttachmentCapRejection extends Error {}

interface SendMessageResponse {
  id: string;
  role: "assistant";
  content: string;
  model: string;
  tool_calls: ToolCallInfo[];
  tool_call_count: number;
  duration_ms: number;
  conversation_title?: string;
  follow_up_suggestions?: string[];
  /** #5038: the route's "Remember ..." capture saved its row this turn. */
  memory_saved?: boolean;
}

interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  available?: boolean;
}

let messageCounter = 0;
function nextId() {
  return `msg-${++messageCounter}-${Date.now()}`;
}

/** Convert a persisted ConversationMessage into the local ChatMessage shape. */
export function toLocalMessage(m: ConversationMessage, conversationId: string): ChatMessage {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    attachments: m.attachments_meta ? JSON.parse(m.attachments_meta) : undefined,
    tool_calls: m.tool_calls ?? undefined,
    model: m.model ?? undefined,
    duration_ms: m.duration_ms ?? undefined,
    // AIHMSGTIME: created_at is D1's zone-less "YYYY-MM-DD HH:MM:SS" (UTC).
    // new Date() on that form reads it as LOCAL time (10h out in Sydney) or
    // Invalid Date in Safari; d1Date reads it as the UTC instant it is.
    timestamp: d1Date(m.created_at).getTime(),
    conversationId,
    meta: m.meta ?? undefined,
  };
}

/** Parse SSE events from a ReadableStream. */
async function readSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: string, data: Record<string, unknown>) => void,
) {
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split("\n\n");
    buffer = parts.pop()!;

    for (const part of parts) {
      const eventMatch = part.match(/^event:\s*(.+)/m);
      const dataMatch = part.match(/^data:\s*(.+)/m);
      if (!eventMatch || !dataMatch) continue;
      try {
        onEvent(eventMatch[1].trim(), JSON.parse(dataMatch[1]));
      } catch {
        // Skip malformed events
      }
    }
  }
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<string>(AUTO_MODEL);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // #1799: presentation mode for the active conversation. Owned here so the
  // flag can ride the conversation CREATE on first send (a demo sets the mode
  // BEFORE asking) — toggling an existing conversation also PUTs (DataAnalyst).
  const [presentationMode, setPresentationMode] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState<StreamingState | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const modelSelectionTouchedRef = useRef(false);
  // #5038: a finished turn refetches the memory and team fact lists it saved to.
  const queryClient = useQueryClient();

  const chooseModel = useCallback((nextModel: string) => {
    modelSelectionTouchedRef.current = true;
    setModel(nextModel);
  }, []);

  // Preferences choose the starting conversation mode. Loading an existing
  // conversation below still wins, so an explicit conversation choice is not
  // replaced by a later preferences request.
  useEffect(() => {
    void apiFetch<{ default_model?: string }>("/api/preferences")
      .then((prefs) => {
        if (prefs.default_model && !modelSelectionTouchedRef.current) setModel(prefs.default_model);
      })
      .catch(() => {});
  }, []);

  /**
   * #2088: which conversation the in-flight send belongs to. The provider that
   * owns this hook never unmounts, so a send whose stream is still open when
   * the user clears the chat or loads another conversation used to finish into
   * whatever was on screen by then: the previous answer appended to a fresh or
   * loaded transcript. Every write below is gated on the send still owning the
   * surface, and clearChat/loadConversation abort and move the marker on.
   */
  const runRef = useRef(0);

  // A real unmount (sign-out, shell teardown) must not leave a stream reading.
  useEffect(() => () => abortRef.current?.abort(), []);

  /** Load an existing conversation by ID. */
  const loadConversation = useCallback(async (id: string) => {
    abortRef.current?.abort();
    runRef.current += 1;
    setIsLoading(false);
    setStreamingMessage(null);
    try {
      const data = await apiFetch<ConversationDetail>(`/api/conversations/${id}`);
      modelSelectionTouchedRef.current = true;
      setConversationId(id);
      setPresentationMode(data.presentation_mode === 1);
      setModel(data.model);
      setMessages(data.messages.map((m) => toLocalMessage(m, id)));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
      // #2307: rethrow so a caller rehydrating a stored id (the panel, on
      // mount) can tell a missing/forbidden conversation apart from a loaded
      // one and fall back to a new conversation instead of showing an error
      // for a thread the user never asked to open.
      throw err;
    }
  }, []);

  const sendMessage = useCallback(
    async (content: string, attachments?: Attachment[]) => {
      // GH #162 (Marcus Jr): allow attachment-only sends. The composer
      // (DataAnalyst.tsx) already permits send when EITHER content OR
      // attachments are present, and the server-side conversation route
      // accepts attachment-only requests (rejects only when both are empty).
      // The previous content-only guard dropped attachment-only sends silently
      // — composer cleared the file but no request fired, looking like the
      // analyst ignored the upload.
      if ((!content.trim() && !attachments?.length) || isLoading) return;

      const run = runRef.current;
      const owns = () => runRef.current === run;

      setError(null);
      setHasAttachments(!!(attachments?.length));

      // #2292 finding 7: the composer clears its pending attachments as soon
      // as a send fires. When the send 413s on an attachment cap, the files
      // never reached the server, so the caller needs to know to restore
      // them rather than forcing a re-pick. False only for that case.
      let attachmentCapRejected = false;

      // #5038: the lists this run's tools may have written. tool_start and
      // tool_result name the tool but carry no result, so a run that ends
      // without landing its turn (the stream closing before `done`, a stop, an
      // error, a clear or switch taking the surface away) refetches them in
      // `finally`. The lists are the user's, not the conversation's, so this
      // runs before the ownership check.
      const touched = new Set<readonly string[]>();
      let landed = false;
      const noteListEvent = (event: string, data: Record<string, unknown>) => {
        if (event === "done") {
          landed = true;
          invalidateListsSavedByTurn(queryClient, data);
        } else if (event === "tool_start" || event === "tool_result") {
          const key = listTouchedByTool(data.tool);
          if (key) touched.add(key);
        }
      };

      // Build attachment metadata for display (no base64 data)
      const attachmentsMeta = attachments?.map((a) => ({
        type: a.type,
        mediaType: a.mediaType,
        filename: a.filename,
        sizeBytes: a.sizeBytes,
      }));

      // Add optimistic user message
      const userMsg: ChatMessage = {
        id: nextId(),
        role: "user",
        content: content.trim(),
        attachments: attachmentsMeta,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);
      setStreamingMessage({ content: "", liveTools: [], liveCharts: [] });

      try {
        // Create conversation on first message
        let convId = conversationId;
        if (!convId) {
          const conv = await apiMutate<{ id: string; model: string }>(
            "/api/conversations",
            "POST",
            { model, ...(presentationMode ? { presentation_mode: true } : {}) }
          );
          convId = conv.id;
          if (!owns()) return; // cleared while the conversation was being created
          setConversationId(convId);
        }

        // Try SSE streaming first
        const controller = new AbortController();
        abortRef.current = controller;
        const resp = await fetch(`/api/conversations/${convId}/messages`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "Accept": "text/event-stream",
          },
          body: JSON.stringify({
            content: content.trim(),
            model,
            ...(attachments?.length ? { attachments } : {}),
          }),
          signal: controller.signal,
        });

        if (!resp.ok) {
          const text = await resp.text();
          // #2292: a 413 from the attachment caps carries a user-facing
          // `message` field (e.g. "Attachments exceed 30 MB combined."),
          // surface that instead of the raw `API 413: {"error":...}` JSON.
          let message = `API ${resp.status}: ${text}`;
          let isCapRejection = false;
          if (resp.status === 413) {
            try {
              const errBody = JSON.parse(text) as { error?: string; message?: string };
              // #2293 adds pdf_too_large, which names the file and the limit.
              // #2371 adds request_too_large, which names the combined size.
              const capErrors = ["attachments_too_large", "too_many_attachments", "pdf_too_large", "request_too_large"];
              if (errBody.error && capErrors.includes(errBody.error) && errBody.message) {
                message = errBody.message;
                isCapRejection = true;
              }
            } catch {
              // Not the expected JSON shape: keep the generic message.
            }
          }
          throw isCapRejection ? new AttachmentCapRejection(message) : new Error(message);
        }

        // Check if we got SSE or JSON fallback
        const contentType = resp.headers.get("content-type") || "";

        if (contentType.includes("text/event-stream") && resp.body) {
          // SSE streaming path
          const reader = resp.body.getReader();

          await readSSEStream(reader, (event, data) => {
            noteListEvent(event, data);
            if (!owns()) return;
            switch (event) {
              case "text_delta":
                setStreamingMessage((prev) => prev ? {
                  ...prev,
                  content: prev.content + (data.text as string),
                } : prev);
                break;

              case "attachment_catalogue": {
                // #2090: the server catalogued a large upload — stamp the
                // summary onto the optimistic user message so the chip shows it now.
                const filename = data.filename as string;
                const sheets = data.sheets as AttachmentCatalogueSummary["sheets"];
                setMessages((prev) => prev.map((m) =>
                  m.id === userMsg.id && m.attachments
                    ? { ...m, attachments: m.attachments.map((a) => a.filename === filename ? { ...a, catalogue: { sheets } } : a) }
                    : m,
                ));
                break;
              }

              case "attachment_catalogue_error": {
                // #2298 F9: a spreadsheet too large or unparsable to
                // catalogue used to fail silently for the person who picked
                // it (the model still got a text note). Stamp the reason
                // onto the same attachment so the chip can show it.
                const filename = data.filename as string;
                const reason = data.reason as string;
                setMessages((prev) => prev.map((m) =>
                  m.id === userMsg.id && m.attachments
                    ? { ...m, attachments: m.attachments.map((a) => a.filename === filename ? { ...a, catalogueError: reason } : a) }
                    : m,
                ));
                break;
              }

              case "attachment_convert_error": {
                // #2555: a PDF whose text conversion failed used to vanish
                // silently and the model then asked for a re-attach that
                // could not work. Same chip, amber, names the file and why.
                const filename = data.filename as string;
                const reason = data.reason as string;
                const bytes = data.bytes as number | undefined;
                setMessages((prev) => prev.map((m) =>
                  m.id === userMsg.id && m.attachments
                    ? { ...m, attachments: m.attachments.map((a) => a.filename === filename ? { ...a, conversion_status: "failed", conversion_reason: reason, bytes } : a) }
                    : m,
                ));
                break;
              }

              case "tool_start":
                setStreamingMessage((prev) => prev ? {
                  ...prev,
                  liveTools: [...prev.liveTools, {
                    tool: data.tool as string,
                    input_summary: data.input_summary as string,
                    status: "running",
                  }],
                } : prev);
                break;

              case "tool_result":
                setStreamingMessage((prev) => {
                  if (!prev) return prev;
                  const tools = [...prev.liveTools];
                  // Find the last running tool with this name
                  for (let i = tools.length - 1; i >= 0; i--) {
                    if (tools[i].tool === data.tool && tools[i].status === "running") {
                      tools[i] = {
                        ...tools[i],
                        status: "done",
                        row_count: data.row_count as number | undefined,
                        execution_ms: data.execution_ms as number | undefined,
                      };
                      break;
                    }
                  }
                  return { ...prev, liveTools: tools };
                });
                break;

              case "tool_chart":
                setStreamingMessage((prev) => prev ? {
                  ...prev,
                  liveCharts: [...prev.liveCharts, {
                    spec: data.chart,
                    sqlQuery: data.sql_query as string | undefined,
                  }],
                } : prev);
                break;

              case "thinking":
                // Heartbeat — AI is still working on a multi-tool analysis
                setStreamingMessage((prev) => prev ? {
                  ...prev,
                  content: prev.content || `Analysing... (round ${data.round}, ${Math.round((data.elapsed_ms as number) / 1000)}s)`,
                } : prev);
                break;

              case "done": {
                const assistantMsg: ChatMessage = {
                  id: data.id as string,
                  role: "assistant",
                  content: data.content as string,
                  tool_calls: data.tool_calls as ToolCallInfo[],
                  model: data.model as string,
                  duration_ms: data.duration_ms as number,
                  timestamp: Date.now(),
                  conversationId: convId!,
                  follow_up_suggestions: data.follow_up_suggestions as string[],
                  meta: (data.meta as ChatTurnMeta | null | undefined) ?? undefined,
                };
                setMessages((prev) => [...prev, assistantMsg]);
                setStreamingMessage(null);
                setIsLoading(false);
                setHasAttachments(false);
                break;
              }

              case "error":
                setError(data.message as string);
                setStreamingMessage(null);
                setIsLoading(false);
                setHasAttachments(false);
                break;
            }
          });

          // If stream ended without a "done" event (Akira 2026-05-26): the SSE
          // connection closed cleanly (HTTP 200, no console errors) but the
          // server never emitted "done", so the streamed content was sitting in
          // `streamingMessage` state. The old code nulled streamingMessage and
          // never promoted to `messages` — visible content vanished, user sees
          // a blank chat with no error.
          //
          // Fix: if there's any streamed content (text, tool_calls, charts),
          // promote it to `messages` as a partial assistant turn so the user
          // sees what got through. Mark with a `_partial: true` flag the UI
          // can surface as "Response cut short — try again" inline.
          if (!owns()) return;
          setStreamingMessage((prev) => {
            if (prev) {
              setIsLoading(false);
              setHasAttachments(false);
              const hasContent =
                (prev.content && prev.content.length > 0) ||
                prev.liveCharts.length > 0;
              if (hasContent && convId) {
                // #199 (c): derive a stream-close reason from the streaming
                // state. This tells the user what was happening when the AI
                // got cut off — running a tool? generating text? — instead
                // of the generic "Response was cut short" message.
                const runningTool = prev.liveTools.find((t) => t.status === "running");
                const lastDoneTool = [...prev.liveTools].reverse().find((t) => t.status === "done");
                const partialReason = runningTool
                  ? `The AI was running the ${runningTool.tool} tool when the connection dropped. ${runningTool.tool === "query_ostendo" ? "This often happens on large Ostendo resources — try filtering more narrowly." : "Try the question again."}`
                  : prev.content && prev.content.length > 0
                  ? lastDoneTool
                    ? `The AI was writing its response after completing ${lastDoneTool.tool}, but the stream dropped before it finished.`
                    : `The AI was writing its response when the stream dropped.`
                  : `The stream dropped before any content was produced.`;

                // Preserve content + charts (no tool_calls since liveTools is
                // streaming-only shape, doesn't match ToolCallInfo). User can
                // retry to get the complete response with tool_calls intact.
                const partialMsg: ChatMessage = {
                  id: nextId(),
                  role: "assistant",
                  content: prev.content || "",
                  charts: prev.liveCharts,
                  model,
                  timestamp: Date.now(),
                  conversationId: convId,
                  _partial: true,
                  _partial_reason: partialReason,
                };
                setMessages((msgs) => [...msgs, partialMsg]);
                setError(`Response was cut short — ${partialReason} The partial content is preserved above; try the same question again for a complete response.`);
              }
            }
            return null;
          });
        } else {
          // JSON fallback path (shouldn't happen normally, but safe)
          const data: SendMessageResponse = await resp.json();
          landed = true;
          invalidateListsSavedByTurn(queryClient, data);
          if (!owns()) return;
          const assistantMsg: ChatMessage = {
            id: data.id,
            role: "assistant",
            content: data.content,
            tool_calls: data.tool_calls,
            model: data.model,
            duration_ms: data.duration_ms,
            timestamp: Date.now(),
            conversationId: convId,
            follow_up_suggestions: data.follow_up_suggestions,
          };
          setMessages((prev) => [...prev, assistantMsg]);
          setStreamingMessage(null);
        }
      } catch (err) {
        // An abort fired by clearChat/loadConversation belongs to a transcript
        // that is no longer on screen, so it leaves nothing behind.
        if (!owns()) return;
        // Don't show abort errors as failures
        if ((err as Error).name === "AbortError") {
          // User cancelled — keep whatever content was streamed so far
          const partial = streamingMessage;
          if (partial?.content) {
            const stoppedMsg: ChatMessage = {
              id: `stopped-${Date.now()}`,
              role: "assistant",
              content: partial.content + "\n\n*(Response stopped)*",
              tool_calls: [],
              timestamp: Date.now(),
              conversationId: conversationId || undefined,
            };
            setMessages((prev) => [...prev, stoppedMsg]);
          }
        } else {
          setError((err as Error).message);
          if (err instanceof AttachmentCapRejection) attachmentCapRejected = true;
        }
        setStreamingMessage(null);
      } finally {
        if (!landed) invalidateLists(queryClient, touched);
        if (owns()) {
          setIsLoading(false);
          setHasAttachments(false);
          abortRef.current = null;
        }
      }
      // #2292 finding 7: tells the caller whether it is safe to drop its
      // pending attachments, false only when an attachment cap 413'd them.
      return !attachmentCapRejected;
    },
    [messages, model, isLoading, conversationId, streamingMessage, presentationMode, queryClient]
  );

  /** Stop the current AI response in progress. */
  const stopResponse = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  const clearChat = useCallback(() => {
    abortRef.current?.abort();
    runRef.current += 1;
    setIsLoading(false);
    setHasAttachments(false);
    setMessages([]);
    setConversationId(null);
    setPresentationMode(false);
    setError(null);
    setStreamingMessage(null);
  }, []);

  return {
    messages,
    isLoading,
    hasAttachments,
    error,
    model,
    setModel: chooseModel,
    sendMessage,
    stopResponse,
    clearChat,
    conversationId,
    loadConversation,
    streamingMessage,
    presentationMode,
    setPresentationMode,
  };
}

export function useChatModels() {
  const [models, setModels] = useState<ModelInfo[]>([]);

  const fetchModels = useCallback(async () => {
    try {
      const data = await apiFetch<ModelInfo[]>("/api/chat/models");
      setModels(data);
    } catch {
      // Fallback to hardcoded list
      setModels([
        { id: AUTO_MODEL, name: "Automatic", provider: "routing" },
        { id: "claude-opus", name: "Claude Opus", provider: "anthropic" },
        { id: "claude-sonnet", name: "Claude Sonnet", provider: "anthropic" },
        { id: "claude-haiku", name: "Claude Haiku", provider: "anthropic" },
      ]);
    }
  }, []);

  return { models, fetchModels };
}
